import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import './Dashboard.css';
import { AuthContext } from '../../context/AuthContext';
import { BLOCKCHAIN_ICONS } from './blockchainIcons';
import DashboardStats from './DashboardStats';
import { APP_CONFIG } from "../../config/appConfig";

const BASE_URL = APP_CONFIG.API_BASE_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshAuth } = useContext(AuthContext);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  // Helper function to check if plan is expired
  const isPlanExpired = (planExpires) => {
    if (!planExpires) return false; // null = lifetime
    return new Date(planExpires) < new Date();
  };

  // Calculate days until expiry (can be negative)
  const getDaysUntilExpiry = (planExpires) => {
    if (!planExpires) return 'Never'; // lifetime
    const expiryDate = new Date(planExpires);
    const now = new Date();
    const diffTime = expiryDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  useEffect(() => {
    // Wait until auth state is resolved
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  // Without a purchased plan this page has nothing to show - go to Wallets
  useEffect(() => {
    const hasActivePlan = Boolean(user?.planId) && user?.status === 'active';
    if (!authLoading && user && !hasActivePlan) {
      navigate('/wallets', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const handleDownload = async () => {
    setGeneratingLink(true);
    setDownloadError('');

    try {
      const response = await fetch(`${BASE_URL}/download/generate-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.expired) {
          setDownloadError('Your plan has expired. Please renew to continue.');
        } else {
          setDownloadError(data.error || 'Failed to generate download link');
        }
        return;
      }

      if (data.downloadUrl) {
        // Refresh auth context to get the new download URL and expiry
        await refreshAuth();
        // Open the download link
        window.open(data.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Error generating download link:', error);
      setDownloadError('Failed to generate download link. Please try again.');
    } finally {
      setGeneratingLink(false);
    }
  };

  if (authLoading) {
    return (
      <DashboardLayout>
        <div className="loading-spinner"></div>
      </DashboardLayout>
    );
  }

  // Check if user is authenticated
  if (!user) {
    return (
      <DashboardLayout>
        <div className="no-plan-message">
          <h1>Please sign in</h1>
          <p>You need to be signed in to view the dashboard</p>
          <button
            className="download-button"
            onClick={() => navigate('/login')}
            style={{ marginTop: '24px' }}
          >
            Sign In
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const hasActivePlan = user?.planId && user?.status === 'active';
  const planExpired = hasActivePlan && isPlanExpired(user?.plan_expires);
  const daysUntilExpiry = user?.plan_expires ? getDaysUntilExpiry(user.plan_expires) : null;

  // EXPIRED PLAN STATE
  if (planExpired) {
    return (
      <DashboardLayout>
        <div className="no-plan-message">
          <h1>Your Plan Has Expired</h1>
          <p style={{ marginTop: '16px', color: '#ABACAA' }}>
            Your subscription expired {Math.abs(daysUntilExpiry)} day{Math.abs(daysUntilExpiry) !== 1 ? 's' : ''} ago.
          </p>
          <p style={{ marginTop: '12px', color: '#ABACAA' }}>
            Renew your plan to continue accessing premium features and downloads.
          </p>
          <button
            className="download-button"
            onClick={() => navigate('/purchase')}
            style={{ marginTop: '32px' }}
          >
            Renew Plan
          </button>
          <button
            className="secondary-button"
            onClick={() => window.open('https://t.me/lunasearch', '_blank')}
            style={{ marginTop: '16px' }}
          >
            Contact Support
          </button>
        </div>
      </DashboardLayout>
    );
  }

  // No purchased plan - the effect above redirects to /wallets
  if (!hasActivePlan) {
    return null;
  }

  return (
    <DashboardLayout>
      <h1>Dashboard</h1>

      <div className="plan-info">
          <div className="info-column">
            <span className="info-label">CURRENT PLAN</span>
            <span className="info-value">{capitalizeFirstLetter(user?.planId)}</span>
            
            <div className="download-section">
              <span className="info-label">DOWNLOAD</span>
              <span className="download-text">Application File</span>
              <button 
                className="download-button"
                onClick={handleDownload}
                disabled={generatingLink}
              >
                {generatingLink ? 'Generating Link...' : 'Download LunaSearch'}
              </button>
              {downloadError && (
                <span style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px' }}>
                  {downloadError}
                </span>
              )}
            </div>
          </div>

          <div className="info-column">
            <span className="info-label">EXPIRES IN</span>
            <span className="info-value">
              {user?.plan_expires === null || user?.plan_expires === undefined
                ? 'Never'
                : `${getDaysUntilExpiry(user.plan_expires)} Days`}
            </span>
          </div>

          <div className="info-column">
            <span className="info-label">BLOCKCHAINS</span>
            <div className="blockchain-list">
              {Array.isArray(user?.blockchains) && user.blockchains.length > 0 ? (
                user.blockchains.map((chain) => (
                  <img
                    key={chain}
                    src={BLOCKCHAIN_ICONS[chain]}
                    alt={chain}
                    className="blockchain-icon"
                    draggable="false"
                    style={{ userSelect: 'none', pointerEvents: 'none' }}
                  />
                ))
              ) : (
                <span className="info-value" style={{ color: '#ABACAA', fontSize: '15px' }}>None</span>
              )}
            </div>
          </div>
        </div>

        <DashboardStats />
    </DashboardLayout>
  );
};

export default Dashboard;